import './expenses.css';
import { useEffect, useState } from 'react';
import { api, queryString } from '../../lib/api-client.js';
import { publicSourcePage } from '../../lib/public-source-page.js';
import { money, number, date } from '../../format.js';
import { useApiQuery, useUrlFilters } from '../../hooks.js';
import { Button, DataTable, FilterPanel, Loading, MetricCard, Pagination, RequestError, SelectField, SourceLink, TextField } from '../../components/ui.js';
import { ProfileIcon } from '../../components/profile-icon.js';
import { ChartBarMark, ChartTooltipGroup, ChartTooltipSvgMark } from '../../components/chart-tooltip.js';

interface ExpenseItem {
  id: number | string;
  year?: number | string | null;
  month?: number | string | null;
  category?: string | null;
  amount?: number | string | null;
  supplier?: string | null;
  description?: string | null;
  document_url?: string | null;
  source_url?: string | null;
}

interface ExpenseSummary {
  value?: number | null;
  records?: number;
  complete?: boolean;
  updated_at?: string | null;
  source_url?: string | null;
  note?: string;
}

interface MonthlyValue {
  label?: string;
  value: number;
  month?: number | string;
  year?: number | string;
}

interface CategoryValue {
  label: string;
  value: number;
}

interface CategorySegment extends CategoryValue {
  color: string;
  colorIndex: number;
  percent: number;
}

interface ExpensesResponse {
  items: ExpenseItem[];
  total: number;
  page: number;
  size: number;
  summary?: ExpenseSummary;
  monthly?: MonthlyValue[];
  categories?: CategoryValue[];
  category_options?: string[];
  period?: { all_years?: boolean; years?: number[]; months?: number; end_year?: number; end_month?: number };
}

interface ExpensesProps {
  id: number;
  year: number;
  allYears: boolean;
  source: string;
}

const pageSize = 10;
const exportPageSize = 100;
const chartColors = ['#075c49', '#21845f', '#4b9a70', '#78b18d', '#a8cbb3', '#c9ddce', '#557d69', '#8b9b75'];

function CategoryDonut({ segments, total }: { segments: CategorySegment[]; total: number }) {
  const radius = 72;
  const circumference = 2 * Math.PI * radius;
  let offset = 0;
  return <ChartTooltipGroup className="expenses-donut-tooltip-group">
    <div className="expenses-donut-shell">
      <svg className="expenses-donut" viewBox="0 0 200 200" role="group" aria-label={`Distribuição por categoria: ${segments.map((item) => `${item.label}, ${item.percent.toFixed(1)} por cento, ${money(item.value)}`).join('; ')}`}>
        {segments.map((item) => {
          const arc = circumference * item.percent / 100;
          const currentOffset = offset;
          offset += arc;
          const percentage = new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 1 }).format(item.percent);
          return <ChartTooltipSvgMark
            className="expenses-donut-segment"
            key={item.label}
            datum={{ title: item.label, label: 'Participação', value: `${percentage}%`, color: item.color, context: `Valor registrado: ${money(item.value)}` }}
          >
            <circle cx="100" cy="100" r={radius} fill="none" stroke={item.color} strokeWidth="48" strokeDasharray={`${arc} ${circumference - arc}`} strokeDashoffset={-currentOffset} transform="rotate(-90 100 100)" />
          </ChartTooltipSvgMark>;
        })}
      </svg>
      <span className="expenses-donut-total"><strong>{money(total)}</strong><small>Total registrado</small></span>
    </div>
  </ChartTooltipGroup>;
}

function periodLabel(item: ExpenseItem) {
  const month = Number(item.month);
  const year = Number(item.year);
  return Number.isInteger(month) && month >= 1 && month <= 12 && Number.isInteger(year)
    ? `${String(month).padStart(2, '0')}/${year}`
    : 'Não informada';
}

function amountValue(value: unknown) {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
}

function csvCell(value: unknown) {
  let text = value === null || value === undefined ? '' : String(value);
  if (/^[\s]*[=+@\-\t\r]/.test(text)) text = `'${text}`;
  return `"${text.replaceAll('"', '""')}"`;
}

function expenseCsv(items: ExpenseItem[], valueLabel: string) {
  const header = ['Data', 'Categoria', 'Fornecedor', 'Descrição', valueLabel, 'Documento', 'Fonte oficial'];
  const lines = items.map((item) => {
    const amount = amountValue(item.amount);
    return [
      periodLabel(item),
      item.category ?? '',
      item.supplier ?? '',
      item.description || 'Não informada pela fonte',
      amount === null ? '' : amount.toFixed(2).replace('.', ','),
      item.document_url ?? '',
      item.source_url ?? '',
    ].map(csvCell).join(';');
  });
  return `\uFEFF${[header.map(csvCell).join(';'), ...lines].join('\r\n')}`;
}

export function ExpensesTab({ id, year, allYears, source }: ExpensesProps) {
  const { params, setValue, updateMany } = useUrlFilters();
  const months = ['6', '12', '24'].includes(params.get('months') ?? '') ? params.get('months')! : '12';
  const page = Number(params.get('page') ?? 1) || 1;
  const category = params.get('category') ?? '';
  const q = params.get('q') ?? '';
  const sort = ['date_desc', 'date_asc', 'amount_desc', 'amount_asc', 'supplier', 'category'].includes(params.get('sort') ?? '') ? params.get('sort')! : 'date_desc';
  const [search, setSearch] = useState(q);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState('');

  useEffect(() => setSearch(q), [q]);
  useEffect(() => {
    if (search === q) return;
    const timer = window.setTimeout(() => updateFilter('q', search, true), 350);
    return () => window.clearTimeout(timer);
  }, [search, q]);

  const requestValues = { year, all_years: allYears, months: Number(months), page, size: pageSize, category, q, sort };
  const query = queryString(requestValues);
  const data = useApiQuery<ExpensesResponse>(['profile-expenses-tab', id, query], `/api/politicians/${id}/expenses?${query}`, true, { keepPreviousData: true });

  function updateFilter(key: string, value: string, replace = false) {
    setValue(key, value, { replace });
  }

  function updatePeriod(value: string) {
    updateMany((next) => {
      next.delete('all_years');
      if (value === 'all') {
        next.set('todos_anos', 'sim');
        next.delete('months');
      } else {
        next.set('todos_anos', 'nao');
        next.set('months', value);
      }
    });
  }

  async function exportAll() {
    if (!data.data || data.data.total <= 0 || exporting) return;
    setExporting(true);
    setExportError('');
    try {
      const first = await api<ExpensesResponse>(`/api/politicians/${id}/expenses?${queryString({ ...requestValues, page: 1, size: exportPageSize })}`);
      const records = [...first.items];
      const pageCount = Math.ceil(first.total / exportPageSize);
      for (let exportPage = 2; exportPage <= pageCount; exportPage += 1) {
        const next = await api<ExpensesResponse>(`/api/politicians/${id}/expenses?${queryString({ ...requestValues, page: exportPage, size: exportPageSize })}`);
        records.push(...next.items);
      }
      if (records.length !== first.total) {
        throw new Error('A quantidade de registros mudou durante a exportação. Atualize a tela e tente novamente.');
      }
      const valueLabel = source === 'senado' ? 'Valor reembolsado (R$)' : source === 'camara' ? 'Valor líquido (R$)' : 'Valor registrado (R$)';
      const blob = new Blob([expenseCsv(records, valueLabel)], { type: 'text/csv;charset=utf-8' });
      const objectUrl = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = objectUrl;
      anchor.download = `despesas-${id}-${year}.csv`;
      anchor.click();
      window.setTimeout(() => URL.revokeObjectURL(objectUrl), 0);
    } catch (error) {
      setExportError(error instanceof Error ? error.message : 'Não foi possível exportar os registros.');
    } finally {
      setExporting(false);
    }
  }

  if (data.isLoading) return <Loading label="Carregando despesas parlamentares…" />;
  if (data.error) return <RequestError message={data.error.message} />;
  const result = data.data;
  if (!result) return null;

  const summary = result.summary;
  const monthly = result.monthly ?? [];
  const categories = result.categories ?? [];
  const count = Number(result.total ?? summary?.records ?? 0);
  const monthTotal = monthly.reduce((sum, item) => sum + Number(item.value || 0), 0);
  const recordedMonthCount = monthly.length;
  const average = recordedMonthCount > 0 ? monthTotal / recordedMonthCount : null;
  const highestMonth = monthly.reduce<MonthlyValue | null>((highest, item) => !highest || Number(item.value) > Number(highest.value) ? item : highest, null);
  const hasNegativeMonth = monthly.some((item) => Number(item.value) < 0);
  const categoryTotal = categories.reduce((sum, item) => sum + Number(item.value || 0), 0);
  const hasNegativeCategory = categories.some((item) => Number(item.value) < 0);
  const categorySegments = !hasNegativeCategory && categoryTotal > 0
    ? categories.map((item, index) => ({ ...item, color: chartColors[index % chartColors.length]!, colorIndex: index % chartColors.length, percent: Number(item.value) / categoryTotal * 100 }))
    : [];
  const maxMonthly = Math.max(...monthly.map((item) => Number(item.value) || 0), 1);
  const valueLabel = source === 'senado' ? 'Valor reembolsado' : source === 'camara' ? 'Valor líquido' : 'Valor registrado';
  const totalLabel = source === 'senado' ? 'Total reembolsado no período' : source === 'camara' ? 'Total líquido no período' : 'Total registrado no período';
  const summaryValue = summary?.value;
  const totalDisplay = summaryValue === null || summaryValue === undefined ? 'Não disponível' : money(summaryValue);
  const maxDisplay = highestMonth ? money(highestMonth.value) : 'Não disponível';
  const maxMonthLabel = highestMonth?.label ?? (highestMonth?.month && highestMonth?.year ? `${String(highestMonth.month).padStart(2, '0')}/${highestMonth.year}` : '');
  const description = source === 'senado'
    ? 'Gastos registrados no CEAPS; o valor exibido corresponde ao reembolso informado pela fonte oficial.'
    : source === 'camara'
      ? 'Gastos registrados no CEAP; o valor exibido corresponde ao valor líquido informado pela fonte oficial.'
      : 'Gastos parlamentares conforme os valores registrados pela fonte oficial.';

  return <div className="profile-content expenses-page">
    <header className="expenses-heading">
      <span className="expenses-heading-icon"><ProfileIcon name="expense-document" size={25} /></span>
      <div><h2>Despesas parlamentares</h2><p>{description}</p></div>
    </header>

    <FilterPanel className="expenses-filters" ariaLabel="Filtros de despesas">
      <div className="expenses-filter-grid">
        <SelectField label="Período" aria-label="Período das despesas" value={allYears ? 'all' : months} onValueChange={updatePeriod}>
          <option value="6">6 meses</option><option value="12">12 meses</option><option value="24">24 meses</option><option value="all">Todos os anos de exercício</option>
        </SelectField>
        <SelectField label="Categoria" aria-label="Categoria das despesas" value={category} onValueChange={(value) => updateFilter('category', value)}>
          <option value="">Todas as categorias</option>{category && !(result.category_options ?? []).includes(category) && <option value={category}>{category}</option>}
          {(result.category_options ?? []).map((item) => <option key={item} value={item}>{item}</option>)}
        </SelectField>
        <TextField label="Buscar" aria-label="Buscar despesas" value={search} onValueChange={setSearch} placeholder="Fornecedor, documento ou descrição" />
        <SelectField label="Ordenar por" aria-label="Ordenar despesas" value={sort} onValueChange={(value) => updateFilter('sort', value)}>
          <option value="date_desc">Mais recentes</option><option value="date_asc">Mais antigas</option><option value="amount_desc">Maior valor</option><option value="amount_asc">Menor valor</option><option value="supplier">Fornecedor</option><option value="category">Categoria</option>
        </SelectField>
      </div>
    </FilterPanel>

    <section className="expenses-summary-grid" aria-label="Resumo das despesas">
      <MetricCard className="expenses-summary-card" label={totalLabel} value={totalDisplay} />
      <MetricCard className="expenses-summary-card" label="Total de registros" value={number(count)} detail="Notas fiscais e reembolsos registrados" />
      <MetricCard className="expenses-summary-card" label="Média dos meses com registros" value={average === null ? 'Não disponível' : money(average)} detail={recordedMonthCount ? `Calculada sobre ${number(recordedMonthCount)} mês(es) com registros` : 'Sem mês registrado para calcular'} />
      <MetricCard className="expenses-summary-card" label="Maior valor em um mês" value={maxDisplay} detail={maxMonthLabel || 'Sem mês registrado'} />
    </section>

    <div className={`expenses-coverage-note${summary?.complete ? ' is-covered' : ' is-partial'}`} role="note">
      <p>{summary?.note ?? 'Os valores refletem o que foi publicado pela fonte oficial e não são corrigidos pelo IPCA.'} {summary?.complete ? 'A cobertura anual da janela consultada está registrada.' : 'A cobertura não está confirmada em todos os anos da janela; ausência de registro não significa valor zero.'}</p>
      {summary?.updated_at && <small>Atualizado em {date(summary.updated_at)} · </small>}
      <SourceLink href={summary?.source_url ?? null} />
    </div>

    <div className="expenses-charts-grid">
      <section className="panel expenses-chart-panel">
        <h3>Despesas mensais</h3><p>Valores em reais conforme registrados pela fonte oficial.</p>
        <div className="expenses-month-chart-panel">
          <h4>Evolução mensal</h4>
          {hasNegativeMonth
            ? <p className="expenses-chart-empty">A série contém ajustes negativos. O gráfico de barras não é exibido para evitar representar esses valores como despesa positiva; os registros estão na tabela.</p>
            : monthly.length ? <ChartTooltipGroup className="expenses-month-chart-tooltip-group"><div className="expenses-month-chart" role="group" aria-label={`Despesas mensais: ${monthly.map((item) => `${item.label ?? ''}, ${money(item.value)}`).join('; ')}`}>
            <div className="expenses-chart-gridlines" aria-hidden="true"><span>{money(maxMonthly)}</span><span>{money(maxMonthly * .66)}</span><span>{money(maxMonthly * .33)}</span><span>R$ 0,00</span></div>
            <div className="expenses-month-bars">{monthly.map((item, index) => {
              const label = item.label ?? (item.month && item.year ? `${String(item.month).padStart(2, '0')}/${item.year}` : `Mês ${index + 1}`);
              const numericValue = Number(item.value) || 0;
              const barHeight = Math.max(numericValue / maxMonthly * 100, 1);
              return <div className="expenses-month-column" key={`${label}-${index}`}>
                <div className="expenses-month-bar-area"><ChartBarMark className="expenses-month-bar-mark" shapeClassName="expenses-month-bar" valuePercent={barHeight} datum={{ title: label, label: 'Despesas mensais', value: money(numericValue), color: '#56866c' }} /></div>
                <small aria-hidden="true">{label}</small>
              </div>;
            })}</div>
          </div></ChartTooltipGroup> : <p className="expenses-chart-empty">Não há registros mensais para os filtros selecionados. Isso não confirma valor zero.</p>}
        </div>
      </section>

      <section className="panel expenses-chart-panel">
        <h3>Despesas por categoria</h3>
        {hasNegativeCategory
          ? <p className="expenses-chart-empty">O total por categoria inclui ajustes negativos, então o gráfico proporcional não é exibido. Os valores permanecem detalhados na tabela.</p>
          : categorySegments.length ? <div className="expenses-category-layout">
            <CategoryDonut segments={categorySegments} total={categoryTotal} />
            <ul className="expenses-category-list">{categorySegments.map((item) => <li className="expenses-category-item" key={item.label}>
              <span className={`expenses-category-dot is-color-${item.colorIndex}`} aria-hidden="true" />
              <span className="expenses-category-name">{item.label}<small>{new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 1 }).format(item.percent)}%</small></span>
              <strong>{money(item.value)}</strong>
          </li>)}</ul>
          </div> : <p className="expenses-chart-empty">Não há valores por categoria para os filtros selecionados. Isso não confirma valor zero.</p>}
      </section>
    </div>

    <section className="panel expenses-detail-panel">
      <div className="expenses-detail-heading"><div><h3>Detalhamento das despesas</h3><p>{number(count)} registros conforme os filtros selecionados.</p></div>
        <Button className="expenses-export-button" onClick={() => void exportAll()} disabled={count === 0 || exporting}>{exporting ? 'Preparando CSV…' : 'Exportar CSV'}</Button>
      </div>
      {exportError && <p className="expenses-export-error" role="alert">{exportError}</p>}
      {result.items.length ? <DataTable tableClassName="expenses-table"><thead><tr><th>Data</th><th>Categoria</th><th>Fornecedor</th><th>Descrição</th><th>{valueLabel}</th><th>Documento</th><th>Fonte oficial</th></tr></thead><tbody>{result.items.map((item, index) => <tr key={`${String(item.id)}-${index}`}>
        <td>{periodLabel(item)}</td><td>{item.category || 'Não informada pela fonte'}</td><td>{item.supplier || 'Não informado pela fonte'}</td><td>{item.description || 'Não informada pela fonte'}</td>
        <td>{item.amount === null || item.amount === undefined ? 'Não disponível' : money(item.amount)}</td>
        <td>{item.document_url ? <a className="source-link" href={publicSourcePage(item.document_url) ?? undefined} target="_blank" rel="noreferrer">Abrir documento ↗</a> : <span className="expenses-unavailable">Não disponível</span>}</td>
        <td><SourceLink href={item.source_url ?? null} /></td>
      </tr>)}</tbody></DataTable> : <p className="expenses-table-empty">Nenhum registro encontrado para estes filtros. A ausência de registros não confirma valor zero.</p>}
      <Pagination page={Number(result.page ?? page)} size={Number(result.size ?? pageSize)} total={Number(result.total ?? 0)} onChange={(nextPage) => updateFilter('page', String(nextPage))} />
    </section>
  </div>;
}
